// src/pages/MarkAttendance.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';

// ─── Helper: Format full name as "SURNAME, First Middle" ───
const formatFullName = (student) => {
  const surname = student.last_name || student.lastName || '';
  const firstName = student.first_name || student.firstName || '';
  const middleName = student.middle_name || student.middleName || '';
  if (!surname && !firstName) return 'N/A';
  const nameParts = [];
  if (surname) nameParts.push(surname + ',');
  if (firstName) nameParts.push(firstName);
  if (middleName) nameParts.push(middleName);
  return nameParts.join(' ');
};

// ─── Helper: Get initials from surname and first name ───
const getInitials = (student) => {
  const surname = student.last_name || student.lastName || '';
  const firstName = student.first_name || student.firstName || '';
  return `${surname.charAt(0) || ''}${firstName.charAt(0) || ''}`.toUpperCase() || 'ST';
};

const MarkAttendance = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState({});
  const [dirtySelections, setDirtySelections] = useState(() => new Set());
  const [remarks, setRemarks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [className, setClassName] = useState('');
  const [teachingDays, setTeachingDays] = useState([]);
  const [terms, setTerms] = useState([]);

  const blankAttendance = (studentList) => Object.fromEntries(
    studentList.map((student) => [student.id, { morning: '', afternoon: '' }])
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [classRes, res, settingsRes, termsRes] = await Promise.all([
          apiClient.get(`/classes/${id}`),
          apiClient.get(`/classes/${id}/students`, { params: { limit: 500 } }),
          apiClient.get('/timetables/settings'),
          apiClient.get('/attendance/terms'),
        ]);
        const classData = classRes.data.data || classRes.data;
        setClassName(classData.name || `Class #${id}`);
        let data = res.data?.data || res.data || [];
        if (data.rows) data = data.rows;
        // Sort by surname first, then first name (matches Students page)
        data = [...data].sort((a, b) => {
          const lastNameA = (a.last_name || a.lastName || '').toUpperCase();
          const lastNameB = (b.last_name || b.lastName || '').toUpperCase();
          if (lastNameA < lastNameB) return -1;
          if (lastNameA > lastNameB) return 1;
          const firstNameA = (a.first_name || a.firstName || '').toUpperCase();
          const firstNameB = (b.first_name || b.firstName || '').toUpperCase();
          if (firstNameA < firstNameB) return -1;
          if (firstNameA > firstNameB) return 1;
          return 0;
        });
        setStudents(data);
        setAttendance(blankAttendance(data));
        setRemarks(Object.fromEntries(data.map((student) => [student.id, ''])));
        setTeachingDays(settingsRes.data?.data?.teaching_days || []);
        const termData = termsRes.data?.data || [];
        setTerms(Array.isArray(termData) ? termData : []);
      } catch (error) {
        toast.error('Failed to load data');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchData();
  }, [id]);

  // Load existing records whenever the teacher changes the date. This stops
  // an old register from being silently overwritten with default values.
  useEffect(() => {
    if (!id || students.length === 0) return;

    let isMounted = true;
    const fetchExistingAttendance = async () => {
      try {
        const response = await apiClient.get(`/attendance/date/${date}`, {
          params: { classId: id },
        });
        const records = response.data?.data || [];
        const recordMap = new Map(
          (Array.isArray(records) ? records : []).map((record) => [
            `${Number(record.student_id)}:${record.session}`,
            record,
          ])
        );

        const nextAttendance = {};
        const nextRemarks = {};
        students.forEach((student) => {
          const morning = recordMap.get(`${Number(student.id)}:morning`);
          const afternoon = recordMap.get(`${Number(student.id)}:afternoon`);
          nextAttendance[student.id] = {
            morning: morning?.status || '',
            afternoon: afternoon?.status || '',
          };
          nextRemarks[student.id] = morning?.remark || afternoon?.remark || '';
        });

        if (isMounted) {
          setAttendance(nextAttendance);
          setRemarks(nextRemarks);
          setDirtySelections(new Set());
        }
      } catch (error) {
        console.error('Failed to load existing attendance:', error);
        toast.error('Failed to load attendance for the selected date');
      }
    };

    fetchExistingAttendance();
    return () => {
      isMounted = false;
    };
  }, [date, id, students]);

  const handleStatusChange = (studentId, session, status) => {
    setAttendance((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || { morning: '', afternoon: '' }),
        [session]: status,
      },
    }));
    setDirtySelections((previous) => {
      const next = new Set(previous);
      next.add(`${studentId}:${session}`);
      return next;
    });
  };

  const handleRemarkChange = (studentId, value) => {
    setRemarks((prev) => ({ ...prev, [studentId]: value }));
  };

  const handleSubmit = async () => {
    if (students.length === 0) {
      toast.error('No students in this class');
      return;
    }

    if (!dateIsValid.isValid) {
      toast.error(dateIsValid.message);
      return;
    }

    if (dirtySelections.size === 0) {
      toast.error('Select Present or Absent for at least one student session.');
      return;
    }

    const payloads = [];
    students.forEach((student) => {
      ['morning', 'afternoon'].forEach((session) => {
        if (!dirtySelections.has(`${student.id}:${session}`)) return;
        payloads.push({
          studentId: student.id,
          classId: parseInt(id),
          date,
          session,
          status: attendance[student.id][session],
          remark: remarks[student.id] || '',
        });
      });
    });

    setSubmitting(true);
    try {
      const results = await Promise.all(
        payloads.map((p) =>
          apiClient.post('/attendance', p).catch((err) => {
            console.error(`Failed for student ${p.studentId}, session ${p.session}:`, err);
            throw err;
          })
        )
      );
      toast.success(`${results.length} attendance session${results.length === 1 ? '' : 's'} saved.`);
      navigate(`/app/classes/${id}/attendance`);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to mark attendance for the selected students'
      );
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const clearSelections = () => {
    setAttendance(blankAttendance(students));
    setDirtySelections(new Set());
  };

  const getTotalPresent = (studentId) => {
    const s = attendance[studentId] || { morning: '', afternoon: '' };
    return (s.morning === 'present' ? 1 : 0) + (s.afternoon === 'present' ? 1 : 0);
  };

  const computeStats = (studentList) => {
    const count = studentList.length;
    const morningPresent = studentList.filter(s => attendance[s.id]?.morning === 'present').length;
    const afternoonPresent = studentList.filter(s => attendance[s.id]?.afternoon === 'present').length;
    const totalPresent = studentList.reduce((acc, s) => acc + getTotalPresent(s.id), 0);
    return { count, morningPresent, afternoonPresent, totalPresent };
  };

  const maleStudents = students.filter(s => s.gender?.toLowerCase() === 'male' || s.gender === 'M');
  const femaleStudents = students.filter(s => s.gender?.toLowerCase() === 'female' || s.gender === 'F');
  const otherStudents = students.filter(s => !['male', 'm', 'female', 'f'].includes(s.gender?.toLowerCase()));

  const maleStats = computeStats(maleStudents);
  const femaleStats = computeStats(femaleStudents);
  const combinedStats = computeStats(students);

  const dateIsValid = useMemo(() => {
    if (!date) return { isValid: false, message: 'Select an attendance date.' };
    const selected = new Date(`${date}T12:00:00`);
    const dayName = selected.toLocaleDateString('en-US', { weekday: 'long' });
    if (teachingDays.length && !teachingDays.some((day) => day.toLowerCase() === dayName.toLowerCase())) {
      return { isValid: false, message: `${dayName} is not one of the school’s configured teaching days.` };
    }
    const term = terms.find((item) => date >= item.start_date && date <= item.end_date);
    if (!term) return { isValid: false, message: 'This date is outside the configured academic term.' };
    const start = new Date(`${term.start_date}T12:00:00`);
    const week = Math.floor((selected - start) / 604800000) + 1;
    if (week < 1 || week > 15) {
      return { isValid: false, message: 'Attendance can only be marked within Weeks 1–15 of the term.' };
    }
    return { isValid: true, term, week, dayName };
  }, [date, teachingDays, terms]);

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="Loading..." />;
  }

  const renderStatusSelect = (studentId, session, status) => {
    return (
      <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1" role="group" aria-label={`${session} attendance`}>
        {['present', 'absent'].map((option) => {
          const selected = status === option;
          const present = option === 'present';
          return (
            <button
              key={option}
              type="button"
              aria-pressed={selected}
              onClick={() => handleStatusChange(studentId, session, option)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold transition-all ${selected ? (present ? 'bg-emerald-600 text-white shadow-sm' : 'bg-rose-600 text-white shadow-sm') : 'bg-transparent text-gray-500 hover:bg-white hover:text-gray-800'}`}
            >
              {selected && (present ? <CheckIcon className="h-3.5 w-3.5" /> : <XMarkIcon className="h-3.5 w-3.5" />)}
              {present ? 'Present' : 'Absent'}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8">
      {/* Simple Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/app/classes/${id}`)}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-600 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              Mark Attendance
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {className} · {students.length} student{students.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="text-sm text-gray-400 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200 whitespace-nowrap">
          {new Date(date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
        </div>
      </div>

      {/* Filters & Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-gray-400" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 text-sm"
          />
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {dateIsValid.isValid && <span className="text-xs font-medium text-primary-700">Week {dateIsValid.week} of 15</span>}
          <button
            onClick={clearSelections}
            className="px-3 py-1.5 text-sm text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Clear selections
          </button>
        </div>
      </div>
      {!dateIsValid.isValid && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          {dateIsValid.message}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Total Students</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-800">{combinedStats.count}</p>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Morning Present</p>
          <p className="text-xl sm:text-2xl font-bold text-green-600">{combinedStats.morningPresent}</p>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Afternoon Present</p>
          <p className="text-xl sm:text-2xl font-bold text-blue-600">{combinedStats.afternoonPresent}</p>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Changes to Save</p>
          <p className="text-xl sm:text-2xl font-bold text-primary-600">{dirtySelections.size}</p>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/80">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Student</th>
                  <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Adm No.</th>
                  <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">AM</th>
                  <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">PM</th>
                  <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remark</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {students.map((student, idx) => {
                  const studentAttendance = attendance[student.id] || { morning: '', afternoon: '' };
                  const total = getTotalPresent(student.id);

                  return (
                    <tr key={student.id} className="hover:bg-gray-50/70 transition-colors">
                      <td className="px-3 sm:px-6 py-3 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-3 sm:px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium text-xs">
                            {getInitials(student)}
                          </div>
                          <span className="font-medium text-gray-800 text-sm">
                            {formatFullName(student)}
                          </span>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-6 py-3 text-sm text-gray-500 font-mono">
                        {student.admission_number || '—'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 text-center">
                        {renderStatusSelect(student.id, 'morning', studentAttendance.morning)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 text-center">
                        {renderStatusSelect(student.id, 'afternoon', studentAttendance.afternoon)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 text-center font-semibold text-gray-700">
                        {studentAttendance.morning && studentAttendance.afternoon ? `${total}/2` : '—'}
                      </td>
                      <td className="px-3 sm:px-6 py-3">
                        <input
                          type="text"
                          placeholder="Remark"
                          value={remarks[student.id] || ''}
                          onChange={(e) => handleRemarkChange(student.id, e.target.value)}
                          className="w-full min-w-[80px] px-2 py-1 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-sm text-gray-700"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50/50 border-t border-gray-200">
                {/* Boys summary */}
                <tr>
                  <td colSpan="3" className="px-3 sm:px-6 py-2 text-sm font-semibold text-blue-700 text-right">
                    Boys ({maleStats.count})
                  </td>
                  <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-blue-700">
                    {maleStats.morningPresent}
                  </td>
                  <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-blue-700">
                    {maleStats.afternoonPresent}
                  </td>
                  <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-blue-700">
                    {maleStats.totalPresent}
                  </td>
                  <td></td>
                </tr>
                {/* Girls summary */}
                <tr>
                  <td colSpan="3" className="px-3 sm:px-6 py-2 text-sm font-semibold text-pink-700 text-right">
                    Girls ({femaleStats.count})
                  </td>
                  <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-pink-700">
                    {femaleStats.morningPresent}
                  </td>
                  <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-pink-700">
                    {femaleStats.afternoonPresent}
                  </td>
                  <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-pink-700">
                    {femaleStats.totalPresent}
                  </td>
                  <td></td>
                </tr>
                {otherStudents.length > 0 && (
                  <tr>
                    <td colSpan="3" className="px-3 sm:px-6 py-2 text-sm font-semibold text-gray-700 text-right">
                      Other ({otherStudents.length})
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-gray-700">
                      {otherStudents.filter(s => attendance[s.id]?.morning === 'present').length}
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-gray-700">
                      {otherStudents.filter(s => attendance[s.id]?.afternoon === 'present').length}
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-center text-sm font-semibold text-gray-700">
                      {otherStudents.reduce((acc, s) => acc + getTotalPresent(s.id), 0)}
                    </td>
                    <td></td>
                  </tr>
                )}
                {/* Grand total */}
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td colSpan="3" className="px-3 sm:px-6 py-3 text-sm font-bold text-primary-700 text-right">
                    Total ({combinedStats.count})
                  </td>
                  <td className="px-3 sm:px-6 py-3 text-center text-sm font-bold text-primary-700">
                    {combinedStats.morningPresent}
                  </td>
                  <td className="px-3 sm:px-6 py-3 text-center text-sm font-bold text-primary-700">
                    {combinedStats.afternoonPresent}
                  </td>
                  <td className="px-3 sm:px-6 py-3 text-center text-sm font-bold text-primary-700">
                    {combinedStats.totalPresent}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={submitting || students.length === 0 || !dateIsValid.isValid || dirtySelections.size === 0}
          className="w-full sm:w-auto px-6 py-2.5 sm:px-8 sm:py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base font-medium shadow-sm hover:shadow-md"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="w-5 h-5" />
              Mark Attendance
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MarkAttendance;